/**
 * useAnalytics — client-side event tracking hook
 *
 * Module-level queue batches events and flushes every 10 s, at 10 events,
 * or on page unload (via fetch keepalive). All operations are async and
 * fire-and-forget — analytics never blocks the UI.
 */
import { useCallback } from 'react';
import apiClient from '../apiClient';

// ── Module-level queue (shared across all hook consumers) ─────────
const queue = [];
let flushTimer = null;
let initialized = false;

const FLUSH_INTERVAL_MS = 10_000;
const MAX_QUEUE_SIZE = 10;

// ── Internal helpers ──────────────────────────────────────────────

function getBaseURL() {
  // Replicate apiClient's baseURL resolution for sendBeacon / fetch keepalive
  const stored = apiClient?.defaults?.baseURL;
  if (stored) return stored;
  return '';
}

function getAuthToken() {
  return localStorage.getItem('authToken') || '';
}

function enqueue(event) {
  try {
    queue.push({
      ...event,
      metadata: event.metadata || {},
      timestamp: new Date().toISOString(),
    });

    if (queue.length >= MAX_QUEUE_SIZE) {
      flush();
    }
  } catch {
    // never throw
  }
}

async function flush() {
  if (queue.length === 0) return;

  // Drain the queue into a local copy
  const batch = queue.splice(0, queue.length);

  try {
    await apiClient.post('/analytics/batch-events', { events: batch });
  } catch {
    // Put events back for retry on next cycle
    queue.unshift(...batch);
  }
}

/**
 * Emergency flush on page unload — uses fetch with keepalive
 * (sendBeacon can't set Authorization headers)
 */
function flushOnUnload() {
  if (queue.length === 0) return;

  const batch = queue.splice(0, queue.length);
  const token = getAuthToken();
  const base = getBaseURL();
  const url = `${base}/api/analytics/batch-events`;

  try {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch {
    // best-effort
  }
}

// ── Lifecycle (called by AnalyticsProvider) ───────────────────────

export function initAnalytics() {
  if (initialized) return;
  initialized = true;

  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnUnload();
  });
  window.addEventListener('pagehide', flushOnUnload);
}

export function destroyAnalytics() {
  if (!initialized) return;
  initialized = false;

  clearInterval(flushTimer);
  flushTimer = null;

  flushOnUnload();

  window.removeEventListener('pagehide', flushOnUnload);
}

// ── The hook ──────────────────────────────────────────────────────

export default function useAnalytics() {
  const trackPageView = useCallback((route) => {
    enqueue({ eventType: 'page_view', route, action: null });
  }, []);

  const trackAction = useCallback((action, route, metadata) => {
    enqueue({ eventType: 'action', action, route: route || null, metadata });
  }, []);

  const trackSearch = useCallback((route, metadata) => {
    enqueue({ eventType: 'search', action: 'search', route: route || null, metadata });
  }, []);

  const trackFormSubmit = useCallback((action, route, metadata) => {
    enqueue({ eventType: 'form_submit', action, route: route || null, metadata });
  }, []);

  return { trackPageView, trackAction, trackSearch, trackFormSubmit };
}
